import React, { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  Settings, Share2, PictureInPicture, WifiOff, Check,
  Facebook, Twitter, MessageCircle, Link2
} from 'lucide-react';
import '../styles/CustomVideoPlayer.css';

/**
 * CustomVideoPlayer - Con Auto-Recovery para Transiciones OBS ↔ Playlist
 *
 * Sistema de 3 URLs con detección automática de transiciones:
 * - Monitorea buffer para detectar stalling (stream trabado)
 * - Auto-recovery cuando detecta errores o transiciones
 * - Reload automático del stream sin intervención del usuario
 *
 * Para URLs unificadas: /hls/{streamKey}-unified/index.m3u8
 */
const CustomVideoPlayer = ({
  src,
  type = 'application/x-mpegURL',
  autoplay = false,
  muted = false,
  isLive = false,
  streamKey = null,
  onReady,
  className = '',
  watermark = null,    // { enabled, url, position, opacity, size }
  ticker = null,       // { enabled, text, fontSize, fontWeight }
  transitions = null,  // { enabled, duration } — fade-out/in between scheduled blocks
  sloopVideo = null,   // { enabled, url, videoName } — emergency fallback loop
  vodId = null         // VOD ID for seek thumbnail support
}) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const progressContainerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const previousVolumeRef = useRef(1);
  const qualitySwitchingRef = useRef(false); // true while an intentional quality switch is in progress
  const manuallySelectedLevelRef = useRef(-1); // level index user manually selected (-1 = auto)
  const qualityBtnRef = useRef(null);
  const shareBtnRef = useRef(null);
  const speedBtnRef = useRef(null);
  const videoControlsRef = useRef(null);
  const [qualityPopupStyle, setQualityPopupStyle] = useState({});
  const [sharePopupStyle, setSharePopupStyle] = useState({});
  const [speedPopupStyle, setSpeedPopupStyle] = useState({});

  // Auto-recovery state
  const lastBufferTimeRef = useRef(0);
  const lastBufferCheckRef = useRef(0);
  const recoveryAttemptsRef = useRef(0);
  const stallingDetectedRef = useRef(false);
  const monitorIntervalRef = useRef(null);
  const stableFragCountRef = useRef(0); // counts consecutive successful fragments after recovery
  const everLoadedFragRef = useRef(false); // true once any fragment has been loaded (startup detection)
  const discontinuityGraceUntilRef = useRef(0); // suppress stall-recovery for N ms after DISCONTINUITY
  const lastFragCCRef = useRef(-1); // track HLS.js continuity counter to detect DISCONTINUITY early
  const suppressSpinnerUntilRef = useRef(0); // suppress loading spinner during DISCONTINUITY transitions

  // Transition fade state
  // 'idle' | 'fading-out' | 'black' | 'fading-in'
  const [transitionPhase, setTransitionPhase] = useState('idle');
  const transitionPhaseRef = useRef('idle');
  const transitionPollRef = useRef(null);

  // Keep a ref to latest transitions config to avoid stale closures
  const transitionsRef = useRef(transitions);
  useEffect(() => { transitionsRef.current = transitions; }, [transitions]);

  const buildProbeUrl = (sourceUrl) => {
    const cleanUrl = sourceUrl.split('?')[0];
    const unifiedMatch = cleanUrl.match(/\/hls\/([^/]+)-master\.m3u8$/);
    if (unifiedMatch) {
      return `/hls/${unifiedMatch[1]}-u-720p.m3u8?_probe=${Date.now()}`;
    }
    return `${cleanUrl}?_probe=${Date.now()}`;
  };

  const markDiscontinuityTransition = (fromCc, toCc) => {
    console.log(`[CustomVideoPlayer] 🔀 DISCONTINUITY incoming (cc ${fromCc}→${toCc}) — letting HLS.js switch sources`);
    discontinuityGraceUntilRef.current = Date.now() + 15000;
    suppressSpinnerUntilRef.current = Date.now() + 15000;
    lastBufferCheckRef.current = Date.now();
    lastBufferTimeRef.current = 0;
    stallingDetectedRef.current = false;
    setIsLoading(false);
  };

  // Audio fade interval ref + volume-before-fade storage
  const audioFadeRef = useRef(null);
  const volumeBeforeFadeRef = useRef(1);

  // Sloop: keep ref to prop to avoid stale closures + force-restart mechanism
  const sloopVideoRef = useRef(sloopVideo);
  useEffect(() => { sloopVideoRef.current = sloopVideo; }, [sloopVideo]);
  const [forceRestartKey, setForceRestartKey] = useState(0);

  // Sloop probe interval (HEAD-checks stream every 30s when offline + sloop active)
  const sloopProbeRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(muted);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [bufferSeconds, setBufferSeconds] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showBigPlay, setShowBigPlay] = useState(!autoplay);
  const [qualityLevels, setQualityLevels] = useState([]);
  const [currentQuality, setCurrentQuality] = useState(-1);
  const [showQualityPopup, setShowQualityPopup] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showSpeedPopup, setShowSpeedPopup] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showOffline, setShowOffline] = useState(false);
  const [centerFeedback, setCenterFeedback] = useState(null);
  const [isAtLive, setIsAtLive] = useState(true);
  const [qualityRevertToast, setQualityRevertToast] = useState(false);

  // Seek thumbnail state
  const [thumbnailsMeta, setThumbnailsMeta] = useState(null); // { interval, count, width, height }
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverX, setHoverX] = useState(0);

  // Initialize HLS player
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    console.log('[CustomVideoPlayer] Initializing with src:', src);

    if (type === 'application/x-mpegURL' && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,

        // Buffer - prioritize smooth playback over low latency.
        // backBufferLength:30  → keep 30s behind current position (recovery/seek).
        // maxBufferLength:60   → pre-load up to 60s ahead of current position.
        //   Was 30s — too small: only ~1.5 segments pre-loaded beyond liveSyncDurationCount.
        //   60s allows the player to pre-fetch many more segments so transient server
        //   delays (video transitions, CPU spikes) don't drain the visible buffer.
        //   For playlist (pre-recorded) the content always exists → fills quickly.
        //   For OBS the live edge caps the fill at liveSyncDurationCount (20s), so
        //   raising maxBufferLength beyond 20s costs nothing extra in OBS latency.
        // maxMaxBufferLength:120 → absolute ceiling HLS.js will never exceed.
        // maxBufferSize: memory cap — 60 MB keeps RAM usage reasonable.
        backBufferLength: 30,
        maxBufferLength: 60,
        maxMaxBufferLength: 120,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,

        // Live sync — count-based only. NEVER mix with liveSyncDuration /
        // liveMaxLatencyDuration (seconds-based) — hls.js throws "Illegal config".
        // liveSyncDurationCount:5   → stay ~20s (5×4s) behind live edge.
        //   Kept at 5 (not 3) — needed for playlist mode: FFmpeg CPU spikes at
        //   video transitions can delay a segment 1-2s; 20s headroom prevents stall.
        // liveMaxLatencyDurationCount:7 → force seek only if >28s (7×4s) behind.
        //   Was 10 (40s) — caused visible 20s jump every ~30-40s of OBS viewing:
        //   buffer filled to 40s → seek to liveSyncDurationCount=20s → user saw
        //   a 20s forward skip in content. At 7 (28s max), the jump shrinks to 8s
        //   (28s→20s) and is much less perceptible.
        // maxLiveSyncPlaybackRate:1.1 → 10% speedup to gently catch up without seek.
        //   Was 1.05 — 5% was too slow; HLS.js fell back to hard seek for minor drift.
        //   At 1.1, small drifts (20-28s range) resolve via gentle speedup, not jump.
        // highBufferWatchdogPeriod:1 → check buffer every 1s for drift/slowdown.
        // driftEvictionDelay:5 → evict fragments causing clock drift after 5s.
        liveSyncDurationCount: 5,
        liveMaxLatencyDurationCount: 7,
        maxLiveSyncPlaybackRate: 1.1,
        highBufferWatchdogPeriod: 1,
        driftEvictionDelay: 5,
        liveDurationInfinity: true,

        // Loading timeouts
        manifestLoadingTimeOut: 10000,
        // 10 retries at 500ms = up to 5s to get a fresh manifest after a source
        // switch; prevents fatal errors caused by a momentary holding manifest.
        manifestLoadingMaxRetry: 10,
        manifestLoadingRetryDelay: 500,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 4,
        levelLoadingRetryDelay: 1000,
        fragLoadingTimeOut: 20000,
        // Safety margin: 10 retries at 500ms intervals = 5s total before fatal.
        // Without this, a single 404 on a just-deleted segment triggers a fatal
        // error that requires full HLS reinitialisation.
        fragLoadingMaxRetry: 10,
        fragLoadingRetryDelay: 500,

        // Nudge: if the player stalls on a segment boundary (gap in timeline),
        // hls.js advances currentTime by nudgeOffset up to nudgeMaxRetries
        // times before giving up. With 4s segments nudging 0.1s resolves most
        // gaps caused by a single missing segment without visible interruption.
        nudgeMaxRetries: 5,
        nudgeDelay: 0.1,

        // ABR — prevent overly-aggressive quality switches in Auto mode.
        // abrEwmaDefaultEstimate: start at 200 kbps so player always begins at
        //   360p. This prevents an initial 720p attempt that buffers immediately
        //   if the connection isn't verified yet, then switches down causing a gap.
        // abrBandWidthFactor: use 90% of measured bandwidth before switching.
        // abrBandWidthUpFactor: very conservative when switching *up* (50%)
        //   — was 0.7 (70%) which caused yo-yoing on connections near the 720p
        //   threshold. 50% means player only upgrades quality when bandwidth is
        //   comfortably double what the target quality needs, preventing oscillation.
        abrEwmaDefaultEstimate: 200000,
        abrBandWidthFactor: 0.9,
        abrBandWidthUpFactor: 0.5,

        debug: false
      });

      hls.loadSource(`${src}${src.includes('?') ? '&' : '?'}t=${Date.now()}`);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        setIsLoading(false);
        setShowOffline(false);

        console.log('[CustomVideoPlayer] Manifest parsed, levels:', data.levels.length);

        // If we were in black/fading-out due to an imminent transition, now fade in
        if (transitionPhaseRef.current === 'black' || transitionPhaseRef.current === 'fading-out') {
          console.log('[CustomVideoPlayer] 🎬 New manifest — starting fade-in');
          const fadeDur = (transitionsRef.current?.duration ?? 1.5) * 1000;
          transitionPhaseRef.current = 'fading-in';
          setTransitionPhase('fading-in');
          setTimeout(() => {
            transitionPhaseRef.current = 'idle';
            setTransitionPhase('idle');
          }, fadeDur);
        }

        // Extract quality levels
        let levels = data.levels.map((level, index) => ({
          index,
          height: level.height || 0,
          width: level.width || 0,
          bitrate: level.bitrate || 0,
          label: level.height > 0 ? `${level.height}p` :
                 level.bitrate > 0 ? `${Math.round(level.bitrate / 1000)}kbps` :
                 'Auto'
        }));

        const hasMultipleValidHeights = levels.filter(l => l.height > 0).length > 1;
        if (hasMultipleValidHeights) {
          levels = levels.filter(level => level.height > 0);
        }

        setQualityLevels(levels);
        // Always start in Auto mode (-1). hls.currentLevel may return 0 before the
        // first ABR decision fires, which would incorrectly show "720p" on init.
        setCurrentQuality(-1);
        manuallySelectedLevelRef.current = -1;

        if (autoplay) {
          video.play().catch(e => {
            console.log('[CustomVideoPlayer] Autoplay prevented:', e);
            setShowBigPlay(true);
          });
        }

        if (onReady) onReady();
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('[CustomVideoPlayer] Fatal error:', data.type, data.details);

          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            console.log('[CustomVideoPlayer] Attempting media error recovery...');
            hls.recoverMediaError();
          } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            // Recreate HLS for errors that mean the source URL is unreachable:
            //   levelLoadError      → variant playlist 404 (OBS disconnected, stream format changed)
            //   manifestLoadError   → master manifest 404 (redirected to dead URL, stale obs state)
            //   manifestLoadTimeOut → manifest request timed out
            // For these cases hls.startLoad() is useless — the instance must be fully rebuilt
            // so it re-follows the unified redirect and picks up the correct source.
            //
            // levelEmptyError → playlist returned 0 segments (source transitioning, holding manifest).
            //   Use startLoad() soft retry instead of destroy/recreate to avoid recovery loops during
            //   source switch startup (OBS transcoder takes 4-8s to generate first segments).
            if (
              data.details === 'levelLoadError' ||
              data.details === 'manifestLoadError' ||
              data.details === 'manifestLoadTimeOut'
            ) {
              console.log(`[CustomVideoPlayer] ${data.details} — stream source unreachable, recreating HLS...`);
              handleStreamRecovery();
            } else if (data.details === 'levelEmptyError') {
              console.log('[CustomVideoPlayer] levelEmptyError — source transitioning, retrying softly...');
              stallingDetectedRef.current = false;
              hls.startLoad();
            } else {
              console.log('[CustomVideoPlayer] Network error - attempting startLoad...');
              hls.startLoad();
            }
          } else {
            setIsLoading(false);
            setShowOffline(true);
          }
        }
      });

      hls.on(Hls.Events.FRAG_LOADED, () => {
        everLoadedFragRef.current = true;
      });

      // Early DISCONTINUITY detection via continuity counter (cc).
      // FRAG_LOADING fires before the buffer adjusts, giving us a head-start to
      // suppress stall recovery while HLS.js handles the source switch.
      hls.on(Hls.Events.FRAG_LOADING, (event, data) => {
        const cc = data?.frag?.cc ?? -1;
        if (lastFragCCRef.current !== -1 && cc !== lastFragCCRef.current) {
          markDiscontinuityTransition(lastFragCCRef.current, cc);
        }
        lastFragCCRef.current = cc;
      });

      // Audio fade on DISCONTINUITY — masks decoder reset clicks and A/V desync
      // that occur when HLS.js flushes the MSE SourceBuffer at a source switch.
      hls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
        if (!data.frag || !data.frag.discontinuity) return;
        // Suppress stall-recovery for 15s after discontinuity so HLS.js can
        // re-buffer the new source without the monitor triggering a full reload.
        discontinuityGraceUntilRef.current = Date.now() + 15000;
        lastBufferCheckRef.current = Date.now();
        lastBufferTimeRef.current = 0;
        stallingDetectedRef.current = false;
        console.log('[CustomVideoPlayer] 🎬 DISCONTINUITY — fading audio to mask decoder reset');
        const savedVolume = video.volume;
        video.volume = 0;
        setTimeout(() => {
          if (!videoRef.current || videoRef.current.muted) return;
          let step = 0;
          const fadeIn = setInterval(() => {
            step++;
            if (videoRef.current && !videoRef.current.muted) {
              videoRef.current.volume = Math.min(savedVolume, (step / 8) * savedVolume);
            }
            if (step >= 8) clearInterval(fadeIn);
          }, 50); // 8 × 50ms = 400ms fade-in
        }, 500); // hold silent 500ms during MSE flush
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        // In Auto mode (manuallySelectedLevelRef = -1) keep showing "Auto" in the UI
        // regardless of which actual ABR level HLS.js picked.
        // Only reflect the level index when the user explicitly chose it.
        if (manuallySelectedLevelRef.current !== -1) {
          setCurrentQuality(data.level);
        }
        qualitySwitchingRef.current = false; // switch complete — re-enable loading spinner
      });

      hlsRef.current = hls;

      return () => {
        console.log('[CustomVideoPlayer] Destroying HLS instance');
        hls.destroy();
      };
    } else if (video.canPlayType(type)) {
      video.src = src;
      setIsLoading(false);

      if (autoplay) {
        video.play().catch(e => {
          console.log('[CustomVideoPlayer] Autoplay prevented:', e);
          setShowBigPlay(true);
        });
      }

      video.addEventListener('error', () => {
        setShowOffline(true);
      });
    }
  }, [src, type, autoplay, onReady, forceRestartKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-Recovery System - Monitors buffer and detects stalling
  useEffect(() => {
    if (!isLive || !hlsRef.current) return;

    const monitorStream = () => {
      const video = videoRef.current;
      const hls = hlsRef.current;
      const now = Date.now();

      // When paused or HLS not ready: reset the timer so accumulated pause-time
      // doesn't trigger a false positive stall when playback eventually resumes.
      // Also skip during the discontinuity grace window (playlist swap re-buffering).
      if (now < discontinuityGraceUntilRef.current) {
        lastBufferCheckRef.current = now;
        lastBufferTimeRef.current = video && video.buffered.length > 0
          ? video.buffered.end(video.buffered.length - 1) : 0;
        return;
      }
      if (!video || !hls || video.paused) {
        lastBufferCheckRef.current = now;
        lastBufferTimeRef.current = video && video.buffered.length > 0
          ? video.buffered.end(video.buffered.length - 1) : 0;
        return;
      }

      const currentBufferEnd = video.buffered.length > 0 ? video.buffered.end(video.buffered.length - 1) : 0;
      const bufferAhead = currentBufferEnd - video.currentTime;

      // Check if buffer is growing
      if (lastBufferCheckRef.current > 0) {
        const timeSinceLastCheck = (now - lastBufferCheckRef.current) / 1000; // seconds
        const bufferGrowth = currentBufferEnd - lastBufferTimeRef.current;

        // Only fire stall if:
        //   1. At least 5s of play-time has passed since last check
        //   2. Buffer ahead is nearly depleted (<2s) — avoids false positives when
        //      buffer is full (HLS.js holds ~30s ahead and stops downloading; buffered.end
        //      doesn't grow even though the stream is healthy)
        //   3. Buffer hasn't grown at all in that window
        if (
          timeSinceLastCheck >= 5 &&
          bufferAhead < 2 &&
          bufferGrowth < 0.5 &&
          !stallingDetectedRef.current
        ) {
          console.warn('[CustomVideoPlayer] ⚠️ Stream stalled for 5s - attempting recovery');
          stallingDetectedRef.current = true;
          handleStreamRecovery();
        }
      }

      lastBufferCheckRef.current = now;
      lastBufferTimeRef.current = currentBufferEnd;
    };

    // Monitor every 2 seconds
    monitorIntervalRef.current = setInterval(monitorStream, 2000);

    return () => {
      if (monitorIntervalRef.current) {
        clearInterval(monitorIntervalRef.current);
      }
    };
  }, [isLive]);

  // Handle stream recovery (reload HLS)
  const handleStreamRecovery = () => {
    const hls = hlsRef.current;
    const video = videoRef.current;

    if (!video) return;

    // ── Sloop fast-path ───────────────────────────────────────────────────────
    // When the Sloop emergency video is configured and no HLS content has ever
    // loaded (fresh start with no active stream), activate the Sloop immediately
    // instead of waiting through 8 long retries (~2 min total startup delays).
    if (
      sloopVideoRef.current?.enabled &&
      sloopVideoRef.current?.url &&
      !everLoadedFragRef.current
    ) {
      console.log('[CustomVideoPlayer] 🆘 Sloop fast-path: stream unavailable, activating emergency video');
      if (hls) { hls.destroy(); hlsRef.current = null; }
      setShowOffline(true);
      setIsLoading(false);
      return;
    }

    if (!hls) return;

    // Maximum 8 recovery attempts — gives ~40s window for scheduled playlist transitions
    // (stop old → clean → start new takes ~8-12s; player needs several retries during that gap)
    if (recoveryAttemptsRef.current >= 8) {
      console.error('[CustomVideoPlayer] ❌ Max recovery attempts reached - showing offline');
      setShowOffline(true);
      setIsLoading(false);
      return;
    }

    // Auto-revert: if user manually selected a quality and we're now recovering,
    // switch back to Auto (ABR) so HLS.js picks a stable quality for this connection.
    if (manuallySelectedLevelRef.current !== -1 && hls) {
      console.log('[CustomVideoPlayer] ↩️ Manual quality stalled — reverting to Auto');
      manuallySelectedLevelRef.current = -1;
      hls.currentLevel = -1;
      setCurrentQuality(-1);
      setQualityRevertToast(true);
      setTimeout(() => setQualityRevertToast(false), 4000);
    }

    recoveryAttemptsRef.current++;
    stableFragCountRef.current = 0; // reset stable fragment counter on each new recovery attempt
    console.log(`[CustomVideoPlayer] 🔄 Recovery attempt ${recoveryAttemptsRef.current}/8`);

    // Silent mode: if a DISCONTINUITY transition just happened, don't show the
    // loading spinner — the user should see the last frame while HLS reinitializes.
    if (Date.now() >= suppressSpinnerUntilRef.current) {
      setIsLoading(true);
    }

    // Save current state
    const wasPlaying = !video.paused;
    const currentVolume = video.volume;
    const wasMuted = video.muted;

    // Destroy and recreate HLS instance
    hls.destroy();

    // Reset buffer monitor timer immediately so the recovery delay (3-8s) does not
    // accumulate as "idle time" and trigger a second false-positive stall when the
    // new HLS instance starts playing.
    lastBufferCheckRef.current = Date.now();
    lastBufferTimeRef.current = 0;

    // Use different delays depending on context:
    // - Transition grace: 500ms — content is likely available, keep the retry quiet.
    // - Startup: longer delays (8s→20s) to wait for FFmpeg to generate segments.
    // - Transition stall: shorter delays (3s→12s) for OBS↔playlist switching.
    const isStartup = !everLoadedFragRef.current;
    const isSilentReload = Date.now() < suppressSpinnerUntilRef.current;
    const delay = isSilentReload
      ? 500
      : isStartup
        ? Math.min(8000 + (recoveryAttemptsRef.current - 1) * 5000, 20000)
        : Math.min(3000 * Math.pow(1.5, recoveryAttemptsRef.current - 1), 12000);
    console.log(`[CustomVideoPlayer] ⏳ Waiting ${Math.round(delay / 1000)}s before retry (${isStartup ? 'startup' : 'transition'} mode)...`);

    setTimeout(() => {
      // Re-check sloop fast-path: branding has likely loaded during the delay.
      // If sloop is now configured and still no fragments → activate immediately
      // instead of creating yet another HLS that will also fail.
      if (
        !everLoadedFragRef.current &&
        sloopVideoRef.current?.enabled &&
        sloopVideoRef.current?.url
      ) {
        console.log('[CustomVideoPlayer] 🆘 Sloop delayed fast-path: activating emergency video (no fragments after delay)');
        setShowOffline(true);
        setIsLoading(false);
        return;
      }

      console.log('[CustomVideoPlayer] ♻️ Recreating HLS instance...');

      const newHls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 30,
        maxBufferLength: 60,
        maxMaxBufferLength: 120,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        liveSyncDurationCount: 5,
        liveMaxLatencyDurationCount: 7,
        maxLiveSyncPlaybackRate: 1.1,
        highBufferWatchdogPeriod: 1,
        driftEvictionDelay: 5,
        liveDurationInfinity: true,
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 10,
        manifestLoadingRetryDelay: 500,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 4,
        levelLoadingRetryDelay: 1000,
        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 10,
        fragLoadingRetryDelay: 500,
        nudgeMaxRetries: 5,
        nudgeDelay: 0.1,
        abrEwmaDefaultEstimate: 200000,
        abrBandWidthFactor: 0.9,
        abrBandWidthUpFactor: 0.5,
        debug: false
      });

      newHls.loadSource(`${src}${src.includes('?') ? '&' : '?'}t=${Date.now()}`);
      newHls.attachMedia(video);
      hlsRef.current = newHls; // critical: update ref so stalling monitor and next recovery use this instance

      newHls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('[CustomVideoPlayer] ✅ Recovery successful - stream reloaded');
        setIsLoading(false);
        setShowOffline(false);
        stallingDetectedRef.current = false;

        // Fade back in if we were in black due to a scheduled transition
        if (transitionPhaseRef.current === 'black' || transitionPhaseRef.current === 'fading-out') {
          const fadeDur = (transitionsRef.current?.duration ?? 1.5) * 1000;
          transitionPhaseRef.current = 'fading-in';
          setTransitionPhase('fading-in');
          setTimeout(() => {
            transitionPhaseRef.current = 'idle';
            setTransitionPhase('idle');
          }, fadeDur);
        }

        // Reset monitoring — use Date.now() NOT 0, because 0 causes the stall monitor
        // to see timeSinceLastCheck = now/1000 (huge) on its next 2s tick and immediately
        // trigger another recovery, creating an infinite reload loop.
        lastBufferTimeRef.current = 0;
        lastBufferCheckRef.current = Date.now();

        // Restore state
        video.volume = currentVolume;
        video.muted = wasMuted;

        if (wasPlaying) {
          video.play().catch(e => {
            console.log('[CustomVideoPlayer] Autoplay after recovery failed:', e);
          });
        }
      });

      newHls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('[CustomVideoPlayer] Fatal error after recovery:', data.type, data.details);

          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            console.log('[CustomVideoPlayer] Attempting media error recovery...');
            newHls.recoverMediaError();
            stallingDetectedRef.current = false;
          } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (
              data.details === 'levelLoadError' ||
              data.details === 'manifestLoadError' ||
              data.details === 'manifestLoadTimeOut'
            ) {
              console.log(`[CustomVideoPlayer] ${data.details} after recovery — retrying...`);
              stallingDetectedRef.current = false;
              if (!everLoadedFragRef.current && sloopVideoRef.current?.enabled && sloopVideoRef.current?.url) {
                // No fragments ever loaded + sloop configured → activate emergency video now
                console.log('[CustomVideoPlayer] 🆘 Sloop activated via recovery error handler');
                newHls.destroy();
                hlsRef.current = null;
                setShowOffline(true);
                setIsLoading(false);
              } else if (everLoadedFragRef.current) {
                // Live transition: chain another recovery
                handleStreamRecovery();
              }
              // else: startup mode without sloop → 10s timer will handle it
            } else if (data.details === 'levelEmptyError') {
              // Source transitioning — soft retry instead of chaining another recovery
              console.log('[CustomVideoPlayer] levelEmptyError after recovery — source transitioning, soft retry...');
              stallingDetectedRef.current = false;
              newHls.startLoad();
            } else {
              console.log('[CustomVideoPlayer] Network error after recovery - will retry via monitor...');
              stallingDetectedRef.current = false;
            }
          } else {
            setIsLoading(false);
            setShowOffline(true);
          }
        }
      });

      newHls.on(Hls.Events.FRAG_LOADED, () => {
        everLoadedFragRef.current = true;
        // Only reset recovery counter after 10 consecutive successful fragments.
        if (recoveryAttemptsRef.current > 0) {
          stableFragCountRef.current++;
          if (stableFragCountRef.current >= 10) {
            console.log('[CustomVideoPlayer] ✅ 10 stable fragments loaded - resetting recovery counter');
            recoveryAttemptsRef.current = 0;
            stableFragCountRef.current = 0;
          }
        } else {
          stableFragCountRef.current = 0;
        }
      });

      newHls.on(Hls.Events.FRAG_LOADING, (event, data) => {
        const cc = data?.frag?.cc ?? -1;
        if (lastFragCCRef.current !== -1 && cc !== lastFragCCRef.current) {
          markDiscontinuityTransition(lastFragCCRef.current, cc);
        }
        lastFragCCRef.current = cc;
      });

      newHls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
        if (!data.frag || !data.frag.discontinuity) return;
        const video = videoRef.current;
        if (!video || video.paused || video.muted) return;
        console.log('[CustomVideoPlayer] 🎬 DISCONTINUITY (recovery) — fading audio to mask decoder reset');
        const savedVolume = video.volume;
        video.volume = 0;
        setTimeout(() => {
          if (!videoRef.current || videoRef.current.muted) return;
          let step = 0;
          const fadeIn = setInterval(() => {
            step++;
            if (videoRef.current && !videoRef.current.muted) {
              videoRef.current.volume = Math.min(savedVolume, (step / 8) * savedVolume);
            }
            if (step >= 8) clearInterval(fadeIn);
          }, 50);
        }, 500);
      });

      hlsRef.current = newHls;
    }, delay);
  };

  // Transition fade polling — checks when the next scheduled playlist change is
  // and applies a smooth fade-out → black → fade-in so the viewer never sees a hard cut.
  useEffect(() => {
    if (!isLive || !streamKey) return;

    const setPhase = (phase) => {
      transitionPhaseRef.current = phase;
      setTransitionPhase(phase);
    };

    const poll = async () => {
      // Only run when currently idle (don't interrupt an in-progress fade)
      if (transitionPhaseRef.current !== 'idle') return;
      // Skip if transitions are disabled in settings
      if (transitionsRef.current?.enabled === false) return;
      try {
        const res = await fetch(`/api/playlists/upcoming-transition?streamKey=${streamKey}&lookahead=12`);
        const data = await res.json();
        if (!data.hasTransition) return;

        const s = data.secondsUntil;
        // Start fading out when ≤8s until transition boundary
        if (s <= 8) {
          const fadeDur = (transitionsRef.current?.duration ?? 1.5) * 1000;
          console.log(`[CustomVideoPlayer] 🎬 Transition in ${s}s — starting fade-out (${fadeDur}ms)`);
          setPhase('fading-out');
          // After fade-out animation, hold on black
          setTimeout(() => setPhase('black'), fadeDur);
          // After 15s max, force fade-in even if no MANIFEST event fired
          setTimeout(() => {
            if (transitionPhaseRef.current !== 'idle') {
              console.log('[CustomVideoPlayer] 🎬 Force fade-in after timeout');
              setPhase('fading-in');
              setTimeout(() => setPhase('idle'), fadeDur);
            }
          }, 15000);
        }
      } catch (_) {}
    };

    // Poll every 3 seconds
    transitionPollRef.current = setInterval(poll, 3000);
    poll(); // run immediately

    return () => clearInterval(transitionPollRef.current);
  }, [isLive, streamKey]);

  // ── Audio fade synchronized with transition overlay ──────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !transitionsRef.current?.enabled) return;

    // Clear any running fade
    if (audioFadeRef.current) {
      clearInterval(audioFadeRef.current);
      audioFadeRef.current = null;
    }

    const fadeDur = (transitionsRef.current?.duration ?? 1.5) * 1000;
    const steps = Math.max(10, Math.round(fadeDur / 50)); // ~50ms per step
    const stepTime = fadeDur / steps;

    if (transitionPhase === 'fading-out') {
      volumeBeforeFadeRef.current = video.volume;
      let step = 0;
      audioFadeRef.current = setInterval(() => {
        step++;
        video.volume = Math.max(0, volumeBeforeFadeRef.current * (1 - step / steps));
        if (step >= steps) { clearInterval(audioFadeRef.current); audioFadeRef.current = null; }
      }, stepTime);
    } else if (transitionPhase === 'black') {
      video.volume = 0;
    } else if (transitionPhase === 'fading-in') {
      const target = volumeBeforeFadeRef.current || volume;
      let step = 0;
      audioFadeRef.current = setInterval(() => {
        step++;
        video.volume = Math.min(target, target * (step / steps));
        if (step >= steps) {
          clearInterval(audioFadeRef.current);
          audioFadeRef.current = null;
          video.volume = target;
        }
      }, stepTime);
    } else if (transitionPhase === 'idle') {
      // Restore volume if interrupted mid-fade
      const target = volumeBeforeFadeRef.current || volume;
      if (video.volume < target) video.volume = target;
    }
  }, [transitionPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sloop startup timer ───────────────────────────────────────────────────
  // Fires 10 s after mount (counted from when src is set).  Activates the
  // emergency video if zero HLS fragments have been loaded yet.
  //
  // Deliberately depends only on `src` (not on sloopVideo prop) so the timer
  // starts immediately at mount regardless of async branding load timing.
  // Uses sloopVideoRef.current at fire-time to read the latest branding value —
  // branding is always loaded well within 10 s on any reasonable connection.
  useEffect(() => {
    if (!src) return;

    const timer = setTimeout(() => {
      if (everLoadedFragRef.current) return; // stream loaded successfully — do nothing
      const sloop = sloopVideoRef.current;
      if (!sloop?.enabled || !sloop?.url) return; // no sloop configured
      console.log('[CustomVideoPlayer] 🆘 Sloop startup timer: no stream after 10s — activating emergency video');
      const hls = hlsRef.current;
      if (hls) { hls.destroy(); hlsRef.current = null; }
      setShowOffline(true);
      setIsLoading(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!src || showOffline || everLoadedFragRef.current) return;
    const sloop = sloopVideoRef.current;
    if (!sloop?.enabled || !sloop?.url) return;

    const timer = setTimeout(() => {
      if (showOffline || everLoadedFragRef.current) return;
      console.log('[CustomVideoPlayer] 🆘 Sloop configured after startup — activating emergency video');
      const hls = hlsRef.current;
      if (hls) { hls.destroy(); hlsRef.current = null; }
      setShowOffline(true);
      setIsLoading(false);
    }, 12000);

    return () => clearTimeout(timer);
  }, [src, showOffline, sloopVideo?.enabled, sloopVideo?.url]);

  // ── Sloop probe: GET-check stream every 30s while offline + sloop active ─
  // Uses GET (not HEAD) so we can inspect the manifest body and confirm it has
  // actual segments (#EXTINF).  The unified URL always returns HTTP 200 even
  // when there is no active stream (holding manifest with 0 segments), so a
  // HEAD/status-only check would incorrectly conclude the stream is back online.
  useEffect(() => {
    if (!showOffline || !sloopVideo?.enabled || !sloopVideo?.url || !isLive || !src) {
      if (sloopProbeRef.current) {
        clearInterval(sloopProbeRef.current);
        sloopProbeRef.current = null;
      }
      return;
    }

    console.log('[CustomVideoPlayer] 🔁 Sloop active — will check stream every 30s');

    const probe = async () => {
      try {
        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), 5000);
        const checkUrl = buildProbeUrl(src);
        // GET so we can read the media playlist body and check for real segments
        const res = await fetch(checkUrl, { method: 'GET', signal: ctrl.signal });
        clearTimeout(tid);

        if (!res.ok) {
          console.log(`[CustomVideoPlayer] 🔍 Sloop probe — stream offline (HTTP ${res.status})`);
          return;
        }

        const text = await res.text();
        // Only consider stream "online" if the manifest contains actual media
        // segments (#EXTINF).  A holding manifest (no active stream) has 0 segments.
        if (!text.includes('#EXTINF')) {
          console.log('[CustomVideoPlayer] 🔍 Sloop probe — holding manifest (0 segments), stream still offline');
          return;
        }

        // Stream is back with real content — reinitialise HLS player.
        console.log('[CustomVideoPlayer] ✅ Sloop probe — stream has segments, reinitialising player...');
        clearInterval(sloopProbeRef.current);
        sloopProbeRef.current = null;

        recoveryAttemptsRef.current = 0;
        stallingDetectedRef.current = false;
        everLoadedFragRef.current = false; // treat as fresh startup so stall monitor resets
        lastBufferCheckRef.current = 0;
        lastBufferTimeRef.current = 0;
        lastFragCCRef.current = -1;
        discontinuityGraceUntilRef.current = 0;
        suppressSpinnerUntilRef.current = 0;

        setShowOffline(false);
        setIsLoading(true);
        setForceRestartKey(k => k + 1); // triggers HLS useEffect → new HLS instance
      } catch (_) {
        // Network error / abort — stream still offline, try again next interval
        console.log('[CustomVideoPlayer] 🔍 Sloop probe — fetch error, stream still offline');
      }
    };

    sloopProbeRef.current = setInterval(probe, 30000);

    return () => {
      if (sloopProbeRef.current) {
        clearInterval(sloopProbeRef.current);
        sloopProbeRef.current = null;
      }
    };
  }, [showOffline, sloopVideo?.enabled, sloopVideo?.url, src]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch seek thumbnail metadata when a VOD ID is provided
  useEffect(() => {
    if (!vodId || isLive) { setThumbnailsMeta(null); return; }
    setThumbnailsMeta(null);
    fetch(`/hls/vod_${vodId}/thumbs/metadata.json`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.count > 0) setThumbnailsMeta(data); })
      .catch(() => {});
  }, [vodId, isLive]);

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setShowBigPlay(false);
    };

    const handlePause = () => setIsPlaying(false);

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        setBuffered((bufferedEnd / video.duration) * 100);
        setBufferSeconds(Math.max(0, bufferedEnd - video.currentTime));
      }

      if (isLive && hlsRef.current) {
        const timeDiff = hlsRef.current.liveSyncPosition - video.currentTime;
        setIsAtLive(timeDiff < 10);
      } else if (isLive) {
        const timeDiff = video.duration - video.currentTime;
        setIsAtLive(timeDiff < 10);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    // Suppress the loading spinner for brief waits caused by intentional quality
    // switches — the buffer rebuild is fast and showing the spinner feels jarring.
    const handleWaiting = () => {
      if (!qualitySwitchingRef.current && Date.now() >= suppressSpinnerUntilRef.current) setIsLoading(true);
    };
    const handleCanPlay = () => setIsLoading(false);
    const handlePlaying = () => { setIsLoading(false); qualitySwitchingRef.current = false; };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [isLive]);

  // Auto-hide controls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const showControlsTemporarily = () => {
      setShowControls(true);
      clearTimeout(controlsTimeoutRef.current);

      if (isPlaying) {
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    };

    container.addEventListener('mousemove', showControlsTemporarily);
    container.addEventListener('touchstart', showControlsTemporarily);

    return () => {
      container.removeEventListener('mousemove', showControlsTemporarily);
      container.removeEventListener('touchstart', showControlsTemporarily);
      clearTimeout(controlsTimeoutRef.current);
    };
  }, [isPlaying]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      }
      if (e.code === 'KeyF') {
        toggleFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close all popups when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowQualityPopup(false);
      setShowSharePopup(false);
      setShowSpeedPopup(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Control functions
  const showFeedbackIcon = (type) => {
    setCenterFeedback(type);
    setTimeout(() => setCenterFeedback(null), 500);
  };

  const togglePlay = (withFeedback = true) => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      if (withFeedback) showFeedbackIcon('play');
    } else {
      video.pause();
      if (withFeedback) showFeedbackIcon('pause');
    }
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    video.volume = newVolume;
    video.muted = newVolume === 0;
    setIsMuted(newVolume === 0);

    if (newVolume > 0) {
      previousVolumeRef.current = newVolume;
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;

    if (video.muted || video.volume === 0) {
      video.muted = false;
      const newVolume = previousVolumeRef.current > 0 ? previousVolumeRef.current : 0.5;
      video.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(false);
    } else {
      previousVolumeRef.current = video.volume;
      video.muted = true;
      video.volume = 0;
      setVolume(0);
      setIsMuted(true);
    }
  };

  const handleProgressClick = (e) => {
    if (isLive) return;

    const video = videoRef.current;
    const rect = progressContainerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    video.currentTime = percentage * video.duration;
  };

  const handleProgressMouseMove = (e) => {
    if (!progressContainerRef.current || isLive) return;
    const rect = progressContainerRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHoverTime(pct * duration);
    setHoverX(e.clientX - rect.left);
  };

  const handleProgressMouseLeave = () => setHoverTime(null);

  const goToLive = () => {
    const video = videoRef.current;
    if (hlsRef.current) {
      video.currentTime = hlsRef.current.liveSyncPosition;
    } else {
      video.currentTime = video.duration;
    }
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    const video = videoRef.current;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && video.webkitEnterFullscreen) {
      if (video.webkitDisplayingFullscreen) {
        video.webkitExitFullscreen();
      } else {
        video.webkitEnterFullscreen();
      }
      return;
    }

    if (!document.fullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const togglePictureInPicture = async () => {
    const video = videoRef.current;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch (err) {
      console.error('[CustomVideoPlayer] PiP error:', err);
    }
  };

  // Calculate popup position relative to .video-controls (its actual containing block).
  // .video-controls is position:absolute and is the nearest positioned ancestor of the popup.
  // bottom = controls bar height + gap (popup sits just above the controls bar)
  // right  = distance from the controls bar right edge to the button's right edge
  const calcPopupStyle = (btnRef) => {
    const controls = videoControlsRef?.current;
    if (!controls) return {};
    // Pin popup to the bottom-right of the controls bar (matching controls right padding).
    // This matches the standard UX (YouTube-style): the menu appears in the bottom-right
    // corner above the controls, regardless of which button was pressed.
    const bottomOffset = controls.offsetHeight + 10;
    return {
      position: 'absolute',
      bottom: `${bottomOffset}px`,
      right: '15px',
      left: 'auto',
      top: 'auto',
    };
  };

  const changeQuality = (levelIndex) => {
    if (hlsRef.current) {
      // Track manual selection so auto-revert knows whether to switch back to Auto.
      manuallySelectedLevelRef.current = levelIndex;

      // Mark that a quality switch is in progress so the loading spinner is suppressed.
      qualitySwitchingRef.current = true;

      // Use currentLevel (immediate flush) for manual quality switches.
      // nextLevel was smooth but caused a critical bug: the player kept playing
      // from its existing ~30s buffer at the old quality while the stalling monitor
      // (condition: bufferAhead < 2s) never fired, leaving the player stuck for 30+s
      // if the new quality couldn't keep up with download speed.
      // With currentLevel: buffer is flushed immediately → if the new quality stalls,
      // bufferAhead drops to ~0 within 4s → stalling monitor fires in 5s → recovery.
      // Trade-off: brief loading indicator on switch, but guaranteed recovery if stalling.
      hlsRef.current.currentLevel = levelIndex;

      setCurrentQuality(levelIndex);
      setShowQualityPopup(false);

      // Reset stalling monitor so it evaluates the NEW quality from scratch.
      lastBufferCheckRef.current = Date.now();
      lastBufferTimeRef.current = 0;
      stallingDetectedRef.current = false;

      // Safety: clear the flag after 5s in case LEVEL_SWITCHED never fires
      // (e.g. the requested level is already the active one).
      setTimeout(() => { qualitySwitchingRef.current = false; }, 5000);
    }
  };

  const changeSpeed = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
    setPlaybackRate(rate);
    setShowSpeedPopup(false);
  };

  const shareToSocial = (platform) => {
    const url = window.location.href;
    const text = 'Mira este streaming en vivo!';

    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        window.open(shareUrl, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url).then(() => {
          console.log('[CustomVideoPlayer] Link copied!');
        });
        break;
      default:
        break;
    }
    setShowSharePopup(false);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h}:${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
    }
    return `${m}:${s < 10 ? '0' + s : s}`;
  };

  return (
    <div
      ref={containerRef}
      className={`video-container ${!showControls ? 'hide-controls' : ''} ${className}`}
      onClick={(e) => {
        if (e.target === containerRef.current || e.target === videoRef.current) {
          setShowQualityPopup(false);
          setShowSharePopup(false);
        }
      }}
    >
      <video
        ref={videoRef}
        muted={muted}
        playsInline
        onClick={() => togglePlay(true)}
      />

      {/* Transition Fade Overlay — black during playlist-to-playlist transitions */}
      <div
        className={`transition-fade-overlay ${transitionPhase !== 'idle' ? 'visible' : ''} ${transitionPhase}`}
        style={transitions?.duration ? { '--fade-duration': `${transitions.duration}s` } : {}}
        aria-hidden="true"
      />

      {/* Watermark / Logo Overlay */}
      {watermark?.enabled && watermark?.url && !showOffline && (
        <div className={`watermark-overlay watermark-${watermark.position || 'bottom-right'}`}>
          <img
            src={watermark.url}
            alt=""
            style={{
              opacity: watermark.opacity ?? 0.8,
              width: `${watermark.size ?? 100}px`
            }}
          />
        </div>
      )}

      {/* Ticker (Marquee) Overlay */}
      {ticker?.enabled && ticker?.text && !showOffline && (
        <div className="ticker-overlay" aria-live="off"
             style={{ height: Math.max(32, (ticker.fontSize ?? 14) + 18) + 'px' }}>
          <div className="ticker-track">
            <span className="ticker-segment" style={{
              fontSize: `${ticker.fontSize ?? 14}px`,
              fontWeight: ticker.fontWeight ?? 500
            }}>{ticker.text}</span>
            <span className="ticker-segment" style={{
              fontSize: `${ticker.fontSize ?? 14}px`,
              fontWeight: ticker.fontWeight ?? 500
            }}>{ticker.text}</span>
          </div>
        </div>
      )}

      {/* Big Play Button */}
      {showBigPlay && !isLoading && (
        <div className="big-play-btn" onClick={() => {
          togglePlay(false);
          setShowBigPlay(false);
        }}>
          <Play size={40} />
        </div>
      )}

      {/* Center Feedback Animation */}
      {centerFeedback && (
        <div className="center-feedback animate">
          {centerFeedback === 'play' ? <Play size={40} /> : <Pause size={40} />}
        </div>
      )}

      {/* Quality Auto-Revert Toast */}
      {qualityRevertToast && (
        <div style={{
          position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '6px 14px',
          borderRadius: '6px', fontSize: '13px', zIndex: 50, pointerEvents: 'none',
          whiteSpace: 'nowrap'
        }}>
          Calidad cambiada a Auto
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="loading-spinner" style={{ display: 'flex' }}>
          <div className="spinner"></div>
        </div>
      )}

      {/* Sloop (Emergency Loop) — plays instead of offline message when configured */}
      {showOffline && sloopVideo?.enabled && sloopVideo?.url && (
        <video
          key="sloop-video"
          src={sloopVideo.url}
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'contain', background: '#000', zIndex: 40
          }}
        />
      )}

      {/* Offline Message — only shown when sloop is not configured */}
      {showOffline && !(sloopVideo?.enabled && sloopVideo?.url) && (
        <div className="offline-message show">
          <WifiOff size={80} />
          <h2>Lo sentimos</h2>
          <p>En este momento nuestro canal no está transmitiendo, por favor vuelve más tarde.</p>
        </div>
      )}

      {/* Quality + Buffer badge — top-right, visible with controls */}
      {!showOffline && !isLoading && qualityLevels.length > 0 && (
        <div
          className="quality-buffer-badge"
          style={{ opacity: showControls ? 1 : 0 }}
        >
          {/* Buffer health indicator */}
          {isLive && (
            <div
              className="buffer-health-pill"
              style={{
                backgroundColor:
                  bufferSeconds >= 10 ? 'rgba(34,197,94,0.25)' :
                  bufferSeconds >= 5  ? 'rgba(234,179,8,0.25)' :
                                        'rgba(239,68,68,0.25)',
                borderColor:
                  bufferSeconds >= 10 ? 'rgba(34,197,94,0.5)' :
                  bufferSeconds >= 5  ? 'rgba(234,179,8,0.5)' :
                                        'rgba(239,68,68,0.5)',
                color:
                  bufferSeconds >= 10 ? '#86efac' :
                  bufferSeconds >= 5  ? '#fde047' :
                                        '#fca5a5'
              }}
            >
              <span
                className="buffer-health-dot"
                style={{
                  backgroundColor:
                    bufferSeconds >= 10 ? '#22c55e' :
                    bufferSeconds >= 5  ? '#eab308' :
                                          '#ef4444'
                }}
              />
              {Math.round(bufferSeconds)}s
            </div>
          )}
          {/* Quality label */}
          <div className="quality-label-pill">
            {currentQuality === -1
              ? 'Auto'
              : (qualityLevels.find(l => l.index === currentQuality)?.label ?? 'Auto')}
          </div>
        </div>
      )}

      {/* Controls Overlay Gradient */}
      <div className="controls-overlay"></div>

      {/* Video Controls */}
      <div className="video-controls" ref={videoControlsRef}>
        {/* Live buffer bar — thin strip above controls */}
        {isLive && !showOffline && bufferSeconds > 0 && (
          <div className="live-buffer-track">
            <div
              className="live-buffer-fill"
              style={{
                width: `${Math.min(100, (bufferSeconds / 30) * 100)}%`,
                backgroundColor:
                  bufferSeconds >= 10 ? '#22c55e' :
                  bufferSeconds >= 5  ? '#eab308' :
                                        '#ef4444'
              }}
            />
          </div>
        )}

        {/* Progress Bar */}
        {!isLive && (
          <div
            ref={progressContainerRef}
            className="progress-container"
            onClick={handleProgressClick}
            onMouseMove={handleProgressMouseMove}
            onMouseLeave={handleProgressMouseLeave}
          >
            {/* Seek thumbnail tooltip (with image) */}
            {hoverTime !== null && thumbnailsMeta && (
              <div
                className="seek-thumbnail-tooltip"
                style={{
                  left: Math.max(80, Math.min(hoverX, (progressContainerRef.current?.getBoundingClientRect().width ?? 320) - 80)),
                }}
              >
                <img
                  src={`/hls/vod_${vodId}/thumbs/thumb_${String(Math.max(1, Math.round(hoverTime / thumbnailsMeta.interval))).padStart(4, '0')}.jpg`}
                  alt=""
                  width={thumbnailsMeta.width || 160}
                  height={thumbnailsMeta.height || 90}
                />
                <span className="seek-thumbnail-time">{formatTime(hoverTime)}</span>
              </div>
            )}
            {/* Time-only tooltip when no thumbnails available */}
            {hoverTime !== null && !thumbnailsMeta && (
              <div className="seek-time-tooltip" style={{ left: hoverX }}>
                {formatTime(hoverTime)}
              </div>
            )}
            <div className="progress-bar">
              <div className="buffer-bar" style={{ width: `${buffered}%` }}></div>
              <div className="current-progress" style={{ width: `${(currentTime / duration) * 100}%` }}></div>
              <div className="scrubber" style={{ left: `${(currentTime / duration) * 100}%` }}></div>
            </div>
          </div>
        )}

        {/* Main Controls */}
        <div className="controls-main">
          <div className="controls-left">
            <button className="control-btn" onClick={() => togglePlay(true)}>
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>

            <div className="volume-container">
              <button className="control-btn" onClick={toggleMute}>
                {isMuted || volume === 0 ? <VolumeX size={24} /> : <Volume2 size={24} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>

            <div className="time-display">
              {!isLive ? (
                <span>{formatTime(currentTime)}</span>
              ) : (
                <div
                  className={`live-indicator ${isAtLive ? 'active' : ''}`}
                  onClick={goToLive}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="live-dot"></div>
                  <span>En directo</span>
                </div>
              )}
            </div>
          </div>

          <div className="controls-right">
            {/* Speed control — only for VOD (not live) */}
            {!isLive && (
              <>
                <button
                  ref={speedBtnRef}
                  className="control-btn speed-btn"
                  onMouseDown={e => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = !showSpeedPopup;
                    if (next) setSpeedPopupStyle(calcPopupStyle(speedBtnRef));
                    setShowSpeedPopup(next);
                    setShowQualityPopup(false);
                    setShowSharePopup(false);
                  }}
                >
                  <span className="speed-label">{playbackRate === 1 ? '1×' : `${playbackRate}×`}</span>
                </button>

                <div className={`quality-popup ${showSpeedPopup ? 'show' : ''}`} style={speedPopupStyle} onMouseDown={e => e.stopPropagation()}>
                  <div className="quality-header">Velocidad</div>
                  <div className="quality-options">
                    {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                      <div
                        key={rate}
                        className={`quality-option ${playbackRate === rate ? 'active' : ''}`}
                        onClick={() => changeSpeed(rate)}
                      >
                        <span>{rate === 1 ? 'Normal (1×)' : `${rate}×`}</span>
                        {playbackRate === rate && <Check className="checkmark" size={16} />}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {qualityLevels.length > 1 && (
              <>
                <button
                  ref={qualityBtnRef}
                  className="control-btn"
                  onMouseDown={e => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = !showQualityPopup;
                    if (next) setQualityPopupStyle(calcPopupStyle(qualityBtnRef));
                    setShowQualityPopup(next);
                    setShowSharePopup(false);
                    setShowSpeedPopup(false);
                  }}
                >
                  <Settings size={24} />
                </button>

                <div className={`quality-popup ${showQualityPopup ? 'show' : ''}`} style={qualityPopupStyle} onMouseDown={e => e.stopPropagation()}>
                  <div className="quality-header">Calidad</div>
                  <div className="quality-options">
                    <div
                      className={`quality-option ${currentQuality === -1 ? 'active' : ''}`}
                      onClick={() => changeQuality(-1)}
                    >
                      <span>Auto</span>
                      {currentQuality === -1 && <Check className="checkmark" size={16} />}
                    </div>
                    {qualityLevels.map((level) => (
                      <div
                        key={level.index}
                        className={`quality-option ${currentQuality === level.index ? 'active' : ''}`}
                        onClick={() => changeQuality(level.index)}
                      >
                        <span>{level.label}</span>
                        {currentQuality === level.index && <Check className="checkmark" size={16} />}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div>
              <button
                ref={shareBtnRef}
                className="control-btn"
                onMouseDown={e => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  const next = !showSharePopup;
                  if (next) setSharePopupStyle(calcPopupStyle(shareBtnRef));
                  setShowSharePopup(next);
                  setShowQualityPopup(false);
                  setShowSpeedPopup(false);
                }}
              >
                <Share2 size={24} />
              </button>

              <div className={`share-popup ${showSharePopup ? 'show' : ''}`} style={sharePopupStyle} onMouseDown={e => e.stopPropagation()}>
                <div className="share-header">Compartir</div>
                <div className="share-options">
                  <div className="share-option facebook" onClick={() => shareToSocial('facebook')}>
                    <span className="share-icon"><Facebook size={16} /></span>
                    <span>Facebook</span>
                  </div>
                  <div className="share-option twitter" onClick={() => shareToSocial('twitter')}>
                    <span className="share-icon"><Twitter size={16} /></span>
                    <span>Twitter / X</span>
                  </div>
                  <div className="share-option whatsapp" onClick={() => shareToSocial('whatsapp')}>
                    <span className="share-icon"><MessageCircle size={16} /></span>
                    <span>WhatsApp</span>
                  </div>
                  <div className="share-option copylink" onClick={() => shareToSocial('copy')}>
                    <span className="share-icon"><Link2 size={16} /></span>
                    <span>Copiar enlace</span>
                  </div>
                </div>
              </div>
            </div>

            <button className="control-btn" onClick={togglePictureInPicture}>
              <PictureInPicture size={24} />
            </button>

            <button className="control-btn" onClick={toggleFullscreen}>
              {isFullscreen ? <Minimize size={24} /> : <Maximize size={24} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
